import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Download, CheckCircle, Clock } from 'lucide-react'
import { useCaseStore } from '../../stores'
import { useProfileStore } from '../../stores'
import useQuestionnaireStore from '../../stores/useQuestionnaireStore'
import HIPAADisclaimerModal from '../../components/HIPAADisclaimerModal'
import { formatDate, formatDateMedical, formatDateTime, parseDate, calculateAge } from '../../utils/dateUtils'
import { formatSex } from '../../utils/formatSex'
import { formatAddress } from '../../utils/formatAddress'
import { useI18n } from '../../i18n'
import { extractReportedAnswers, buildReportedSystems, formatReportedSystems } from '../../utils/reviewOfSystems'
import { Capacitor } from '@capacitor/core'
import { sharePdfFromHtml } from '../../utils/sharePdf'
import { currentMedicationsLine } from '../../utils/medicalReportMedications'
import { stripBracketedIcd } from '../../utils/diagnosisText'
import { shownDiagnosisTexts } from '../../utils/signedVisit' // PROD-957 Part B
import { isEncounterNoteHeld } from '../../utils/noteHold' // PROD-1077: the one hold reader
import { asText } from '../../utils/asText' // PROD-1156: a case-plan value is drawn as text, never as an object
import { clinicianFromText, clinicianName, withCaseClinician } from '../../utils/clinicianName.js'

// v242.46.0: Orella navy wordmark (base64) for the printed Medical Report letterhead +
// footer. Embedded (not an asset URL) because the PDF renders in a detached window
// (window.open / sharePdfFromHtml) where hashed asset URLs don't resolve — same
// approach as the MD panel's pdfTemplates.js ORELLA_LOGO_NAVY_BASE64.
const ORELLA_WORDMARK_NAVY = 'data:image/png;base64,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'

// Helper function to format AVS content into styled sections
const formatAVSContent = (avsContent) => {
  if (!avsContent) return null
  
  // Parse AVS text into sections
  const sections = []
  
  // Remove unicode box characters and split into sections
  const cleanContent = avsContent
    .replace(/[━═─┈┉┄┅╌╍]/g, '') // Remove box characters
    .replace(/\n{3,}/g, '\n\n') // Normalize multiple line breaks
  
  // Extract key sections by looking for common headers
  const sectionPatterns = [
    { pattern: /(?:TODAY'?S VISIT|REASON FOR VISIT)([\s\S]*?)(?=YOUR TREATMENT|TREATMENT PLAN|$)/i, title: "Today's Visit", icon: 'stethoscope' },
    { pattern: /(?:YOUR TREATMENT PLAN|TREATMENT PLAN)([\s\S]*?)(?=WARNING|WHEN TO|$)/i, title: 'Your Treatment Plan', icon: 'pill' },
    { pattern: /(?:WARNING SIGNS|WHEN TO SEEK|WHEN TO CALL)([\s\S]*?)(?=FOLLOW-?UP|$)/i, title: 'When to Seek Care', icon: 'alert' },
    { pattern: /(?:FOLLOW-?UP|NEXT STEPS)([\s\S]*?)(?=Thank|Questions|$)/i, title: 'Follow-Up Care', icon: 'calendar' },
  ]
  
  sectionPatterns.forEach(({ pattern, title, icon }) => {
    const match = cleanContent.match(pattern)
    if (match && match[1]) {
      const content = match[1]
        .trim()
        .replace(/^[\s\n•\-]+/, '') // Remove leading bullets/dashes
        .split(/\n/)
        .filter(line => line.trim())
        .map(line => line.replace(/^[•\-\*]\s*/, '').trim())
        .filter(line => line && !line.match(/^[\s\-=]+$/)) // Remove empty/separator lines
      
      if (content.length > 0) {
        sections.push({ title, icon, content })
      }
    }
  })
  
  return sections.length > 0 ? sections : null
}

// ★ PROD-1077: the held card, in place of ONE encounter's note (the hold is per encounter,
//   utils/noteHold.js). ★ s114 (Dr. Rami 2026-10-22, canvas H2/H3 approved): every hold is now "Hold for
//   medical-legal review", so the card says so, in the patient's language (medicalNote.*).
function HeldNoteCard({ doctorName, officeName, officeAddress, officePhone }) {
  const { t } = useI18n()
  return (
    <div className="my-5 rounded-2xl border-2 overflow-hidden" style={{ borderColor: '#46A8F930' }}>
      <div className="px-5 py-4 text-center" style={{ backgroundColor: '#E9F8FF' }}>
        <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: '#46A8F918' }}>
          <svg className="w-6 h-6" style={{ color: '#46A8F9' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <p className="text-[14px] font-bold" style={{ color: '#15354F' }}>
          {t('medicalNote.heldTitle')}
        </p>
        <p className="text-[12px] mt-1" style={{ color: '#64748B' }}>
          {t('medicalNote.heldBody')}
        </p>
      </div>
      <div className="px-5 py-4 bg-white">
        <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: '#94A3B8' }}>
          {t('medicalNote.officeInfo')}
        </p>
        {doctorName && (
          <p className="text-[12.5px] font-semibold mb-1" style={{ color: '#15354F' }}>{doctorName}</p>
        )}
        {officeName && (
          <p className="text-[12px]" style={{ color: '#475569' }}>{officeName}</p>
        )}
        {officeAddress && (
          <p className="text-[12px]" style={{ color: '#475569' }}>{officeAddress}</p>
        )}
        {officePhone && (
          <a href={`tel:${officePhone}`} className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 rounded-lg text-[12px] font-semibold" style={{ backgroundColor: '#E9F8FF', color: '#46A8F9' }}>
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            {officePhone}
          </a>
        )}
        {!officeName && !officePhone && !officeAddress && (
          <p className="text-[12px]" style={{ color: '#94A3B8' }}>{t('medicalNote.contactDirect')}</p>
        )}
      </div>
    </div>
  )
}

export default function MyMedicalNote() {
  const navigate = useNavigate()
  const { currentCase, loadCases } = useCaseStore()
  const profile = useProfileStore((state) => state.profile)
  const { t } = useI18n()
  const [showHIPAAModal, setShowHIPAAModal] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)

  // Refresh case data on mount to get latest finalized data
  useEffect(() => {
    window.scrollTo(0, 0)
    
    // Auto-refresh to get latest case data (especially after MD Panel finalization)
    const refreshData = async () => {
      if (currentCase?.id && !currentCase.id.startsWith('local-')) {
        setIsRefreshing(true)
        try {
          await loadCases()
          console.log('[MyMedicalNote] Refreshed case data from server')
        } catch (err) {
          console.error('[MyMedicalNote] Failed to refresh:', err)
        }
        setIsRefreshing(false)
      }
    }
    refreshData()
  }, [])

  // FHOOK: hook above the early return (rules of hooks); the store hook never reads currentCase.
  const { getSpecialty, getFocusArea } = useQuestionnaireStore()

  if (!currentCase) {
    navigate('/case/home')
    return null
  }

  // Handle both data structures
  const specialtyId = currentCase.specialtyId || currentCase.specialty?.id
  const focusAreaId = currentCase.focusAreaId || currentCase.focusArea?.id
  const focusAreaName = currentCase.focusAreaName || currentCase.focusArea?.name || 'presenting complaint'
  const specialtyName = currentCase.specialtyName || currentCase.specialty?.name || 'General'

  // Determine completion status
  const describeComplete = currentCase.describeComplete || currentCase.describe?.completed
  const detectComplete = currentCase.detectComplete || currentCase.detect?.completed
  const diagnoseComplete = currentCase.diagnose?.completed
  
  // Check for finalized case data from MD Panel
  const isFinalized = currentCase.status === 'completed' || currentCase.finalizedAt
  
  // Parse finalization data - may be JSON in avsContent/afterVisitSummary
  let parsedFinData = null
  const rawAvs = currentCase.avsContent || currentCase.afterVisitSummary || currentCase.diagnose?.avsContent || ''
  if (rawAvs) {
    try {
      const parsed = JSON.parse(rawAvs)
      if (typeof parsed === 'object') parsedFinData = parsed
    } catch {
      // Not JSON, use as plain AVS content
    }
  }
  
  // Debug logging
  console.log('[MyMedicalNote] isFinalized:', isFinalized)
  console.log('[MyMedicalNote] rawAvs length:', rawAvs?.length || 0)
  console.log('[MyMedicalNote] parsedFinData keys:', parsedFinData ? Object.keys(parsedFinData) : 'null')
  console.log('[MyMedicalNote] consultNote available:', !!(parsedFinData?.consultNote || currentCase.diagnose?.consultNote || currentCase.consultNote))
  
  const finalizedData = {
    ...(currentCase.diagnose || {}),
    ...(parsedFinData || {}),
  }
  const doctorConsultComplete = currentCase.doctorNotes?.completed || isFinalized
  
  // Get finalized consultation note and billing codes
  const consultNote = parsedFinData?.consultNote || finalizedData.consultNote || currentCase.consultNote || ''
  const initialConsultNote = parsedFinData?.initialConsultNote || finalizedData.initialConsultNote || currentCase.initialConsultNote || ''
  const priorConsultNote = parsedFinData?.priorConsultNote || finalizedData.priorConsultNote || finalizedData.diagnose?.priorConsultNote || ''
  const consultNoteHistory = parsedFinData?.consultNoteHistory || finalizedData.consultNoteHistory || currentCase.consultNoteHistory || []
  const isFollowUpCase = parsedFinData?.diagnose?.isFollowUp || finalizedData.diagnose?.isFollowUp || currentCase.isFollowUp || false
  const examinationNotes = parsedFinData?.examinationNotes || finalizedData.examinationNotes || ''
  const billingCodes = parsedFinData?.billingCodes || finalizedData.billingCodes || []
  // R14 / PROD-260 (patient P1): what the patient reads of a diagnosis is its text, never the
  // raw ICD-10 code (utils/diagnosisText.js).
  const diagnosisTexts = shownDiagnosisTexts(currentCase, billingCodes) // PROD-957 Part B: the shown visit's
  const cptCodes = parsedFinData?.cptCodes || finalizedData.cptCodes || []
  const followUpData = parsedFinData?.followUp || finalizedData.followUp || currentCase.followUp || {}
  const avsContent = parsedFinData?.avsContent || rawAvs || ''
  
  // ★ PROD-1077: the hold is per ENCOUNTER (utils/noteHold.js). The old case-level reader of the
  //   envelope's noteDelivery was dead (the store never carried it), so a held note was shown.
  const anyEncounterHeld = (currentCase.encounters || []).some((enc) => enc?.finalized === true && isEncounterNoteHeld(enc))
  
  // Get doctor info from finalized data with better fallbacks
  // Parse finalizedBy - it may come as JSON string from API
  let finalizedBy = currentCase.finalizedBy || parsedFinData?.finalizedBy || {}
  if (typeof finalizedBy === 'string') {
    try {
      finalizedBy = JSON.parse(finalizedBy)
    } catch {
      finalizedBy = {}
    }
  }
  
  // Get doctor info from case's assigned doctor if finalizedBy is empty
  const assignedDoctor = currentCase.doctor || currentCase.doctors || {}
  const assignedDoctorName = assignedDoctor.first_name && assignedDoctor.last_name
    ? clinicianName(withCaseClinician(assignedDoctor, currentCase))
    : ''
  
  const doctorName = finalizedBy.name 
    ? clinicianName(finalizedBy, 'formal')
    : assignedDoctorName
    || currentCase.doctorNotes?.physician 
    || (currentCase.claimedBy?.name ? clinicianName(clinicianFromText(currentCase.claimedBy.name)) : '')
    || ''

  // v121.1.0: Doctor office info for held note contact message
  const officePhone = assignedDoctor.practice_phone || assignedDoctor.practicePhone || ''
  const officeName = assignedDoctor.practice_name || assignedDoctor.practiceName || ''
  const officeAddress = [
    // v222.4.8 — defensive: practice_address may be a JSONB dict / JSON-string
    formatAddress(assignedDoctor.practice_address || assignedDoctor.practiceAddress || ''),
    assignedDoctor.practice_city || assignedDoctor.practiceCity || '',
    assignedDoctor.practice_state || assignedDoctor.practiceState || '',
    assignedDoctor.practice_zip || assignedDoctor.practiceZip || '',
  ].filter(Boolean).join(', ')
  
  console.log('[MyMedicalNote] finalizedBy present:', !!finalizedBy)
  console.log('[MyMedicalNote] doctorName present:', !!doctorName)
  
  console.log('[MyMedicalNote] consultNote length:', consultNote?.length || 0)

  // Get specialty and focus area from store - use flexible matching
  const specialty = getSpecialty(specialtyId) || getSpecialty(specialtyName)
  
  // Use store's flexible getFocusArea function
  let focusArea = getFocusArea(specialtyId, focusAreaId) || getFocusArea(specialtyId, focusAreaName)
  // Try with specialty name as fallback
  if (!focusArea && specialtyName) {
    focusArea = getFocusArea(specialtyName, focusAreaId) || getFocusArea(specialtyName, focusAreaName)
  }

  // Helper to get actual question text from questionnaire data
  const getQuestionText = (categoryKey, questionId) => {
    const questions = focusArea?.questions?.[categoryKey] || []
    // Handle prefixed question IDs
    const cleanId = questionId.replace(`${categoryKey}_`, '')
    const question = questions.find(q => q.id === cleanId || q.id === questionId)
    // Questionnaire uses 'text' property
    return question?.text || question?.question || cleanId
  }

  // Get patient uploads
  const patientPhotos = currentCase.detect?.photos || []
  const patientDocuments = currentCase.detect?.documents || []

  // Format date - handle timezone correctly for DOB
  // formatDate for this file uses MM/DD/YYYY (medical document format)
  const formatDateForDoc = (dateString) => formatDateMedical(dateString) || '[Not provided]'
  // ★ v242.145.0 SEX-CAPITALISE-SWEEP-2 — the local copy is gone. utils/formatSex.js
  //   says "do not mint a second one of these" and this was the second one; it
  //   survived because the gate's no-second-formatter arm only scanned the files
  //   already on its own list, which is a description of those files rather than
  //   a rule. The arm is universally quantified now and this import is the fix.

  // calculateAge imported from dateUtils

  // Get patient name from profile
  const getPatientName = () => {
    const firstName = profile?.identification?.firstName || profile?.firstName || ''
    const lastName = profile?.identification?.lastName || profile?.lastName || ''
    if (firstName || lastName) {
      return `${lastName}, ${firstName}`.trim().replace(/^,\s*|,\s*$/, '') || '[Not provided]'
    }
    return '[Not provided]'
  }

  // Generate HPI from questionnaire answers
  const generateHPI = () => {
    const answers = currentCase.answers || {}
    
    let hpiParts = []
    hpiParts.push(`The patient presents for evaluation of ${focusAreaName.toLowerCase()}.`)
    
    // Voice recording transcription
    if (currentCase.voiceRecording?.transcription) {
      hpiParts.push(`\nPatient describes: "${currentCase.voiceRecording.transcription}"`)
    }
    
    // Text input from describe section (if no voice recording or as supplement)
    if (currentCase.voiceTextInput) {
      hpiParts.push(`\nPatient notes: "${currentCase.voiceTextInput}"`)
    }
    
    // Collect all answers - they're stored with prefixed keys like "details_duration"
    const detailAnswers = []
    const symptomAnswers = []
    const contextAnswers = []
    const additionalAnswers = []
    
    Object.entries(answers).forEach(([key, value]) => {
      if (!value || (Array.isArray(value) && value.length === 0)) return
      
      const formattedValue = formatAnswerValue(value)
      
      if (key.startsWith('details_')) {
        const questionId = key.replace('details_', '')
        const questionText = getQuestionText('details', questionId)
        detailAnswers.push(`${questionText}: ${formattedValue}`)
      } else if (key.startsWith('signs_symptoms_')) {
        const questionId = key.replace('signs_symptoms_', '')
        const questionText = getQuestionText('signs_symptoms', questionId)
        // For symptoms, include the question context if it's not just listing symptoms
        if (questionText.toLowerCase().includes('describe') || questionText.toLowerCase().includes('other')) {
          symptomAnswers.push(`${questionText}: ${formattedValue}`)
        } else {
          symptomAnswers.push(formattedValue)
        }
      } else if (key.startsWith('context_')) {
        const questionId = key.replace('context_', '')
        const questionText = getQuestionText('context', questionId)
        contextAnswers.push(`${questionText}: ${formattedValue}`)
      } else if (key.startsWith('additional_factors_')) {
        const questionId = key.replace('additional_factors_', '')
        const questionText = getQuestionText('additional_factors', questionId)
        additionalAnswers.push(`${questionText}: ${formattedValue}`)
      }
    })
    
    // Add sections if they have content
    if (detailAnswers.length > 0) {
      hpiParts.push(`\nSymptom Details:\n${detailAnswers.map(a => `• ${a}`).join('\n')}`)
    }
    
    if (symptomAnswers.length > 0) {
      hpiParts.push(`\nAssociated Symptoms: ${symptomAnswers.join(', ')}`)
    }
    
    if (contextAnswers.length > 0) {
      hpiParts.push(`\nContext & Triggers:\n${contextAnswers.map(a => `• ${a}`).join('\n')}`)
    }
    
    if (additionalAnswers.length > 0) {
      hpiParts.push(`\nAdditional Factors:\n${additionalAnswers.map(a => `• ${a}`).join('\n')}`)
    }
    
    if (!describeComplete && Object.keys(answers).length === 0) {
      hpiParts.push('\n\n[PENDING: Complete Describe section for full HPI]')
    }
    
    return hpiParts.join('\n')
  }

  // Format answer value
  const formatAnswerValue = (value) => {
    if (Array.isArray(value)) return value.join(', ')
    if (typeof value === 'object' && value !== null) {
      if (value.value) return value.details ? `${value.value} - ${value.details}` : value.value
      return JSON.stringify(value)
    }
    return String(value)
  }

  // ★★★ v242.127.0 · ROS-FABRICATED-NEGATIVE-1 — THE THIRD BRANCH IS GONE.
  //
  // This used to end `else -> system.default`, and system.default is a sentence
  // like "No cough, wheezing, or shortness of breath". That sentence is a claim
  // that a clinician asked and the patient denied. NOTHING HERE ASKED. And the
  // matcher is English-only, so a Spanish patient matched no system at all and
  // the note asserted a completely normal review of ten systems for someone who
  // had reported symptoms.
  //
  // The logic now lives in utils/reviewOfSystems.js — pure, so it can be RUN by
  // scripts/check-ros-negatives.mjs. An unmatched system is ABSENT, not normal.
  // v242.165.0 ROS-KEYWORD-MATCH-1 — the system now comes from the QUESTION, via
  // its ids, not from sniffing the answer text. specialtyId and focusAreaId are
  // already resolved at the top of this component (lines 97-98); the old call
  // passed specialtyNAME, which is translated, so the see-HPI branch never fired
  // for a Spanish case either.
  const generateROS = () =>
    formatReportedSystems(
      buildReportedSystems(
        extractReportedAnswers(currentCase.answers || {}, specialtyId, focusAreaId),
        specialtyId
      )
    )

  // Format allergies
  const formatAllergies = () => {
    if (!profile?.allergies) return '[Not documented - Complete Profile]'
    
    const allergies = profile.allergies
    const parts = []
    
    // Food allergies
    if (allergies.hasFoodAllergies === false) {
      parts.push('Food: NKFA (No Known Food Allergies)')
    } else if (allergies.hasFoodAllergies === true && allergies.foodList?.length > 0) {
      const foodItems = allergies.foodList.map(a => {
        let entry = a.name || a
        if (a.severity) entry += ` (${a.severity})`
        if (a.reaction) entry += ` - ${a.reaction}`
        return entry
      }).join(', ')
      parts.push(`Food: ${foodItems}`)
    } else if (allergies.hasFoodAllergies === true) {
      parts.push('Food: Has food allergies (not specified)')
    }
    
    // Medication allergies
    if (allergies.hasMedAllergies === false) {
      parts.push('Medication: NKDA (No Known Drug Allergies)')
    } else if (allergies.hasMedAllergies === true && allergies.medList?.length > 0) {
      const medItems = allergies.medList.map(a => {
        let entry = a.name || a
        if (a.severity) entry += ` (${a.severity})`
        if (a.reaction) entry += ` - ${a.reaction}`
        return entry
      }).join(', ')
      parts.push(`Medication: ${medItems}`)
    } else if (allergies.hasMedAllergies === true) {
      parts.push('Medication: Has drug allergies (not specified)')
    }
    
    // Legacy format support
    if (parts.length === 0) {
      if (allergies.hasAllergies === false) return 'NKDA (No Known Drug Allergies)'
      if (allergies.list?.length > 0) {
        return allergies.list.map(a => {
          let entry = a.name || a
          if (a.severity) entry += ` (${a.severity})`
          if (a.reaction) entry += ` - ${a.reaction}`
          return entry
        }).join('\n')
      }
      return '[Not documented - Complete Profile]'
    }
    
    return parts.join('\n')
  }

  // Format medications
  const formatMedications = () => {
    if (!profile?.medications) return '[Not documented - Complete Profile]'
    if (profile.medications.hasMedications === false) return 'None'
    if (profile.medications.list?.length > 0) {
      return profile.medications.list.map(m => {
        let entry = m.name
        if (m.dosage) entry += ` ${m.dosage}`
        if (m.frequency) entry += `, ${m.frequency}`
        if (m.purpose) entry += ` (for ${m.purpose})`
        return entry
      }).join('\n')
    }
    return '[Not documented - Complete Profile]'
  }

  // Format medical history
  const formatMedicalHistory = () => {
    if (!profile?.medicalHistory) return '[Not documented - Complete Profile]'
    const parts = []
    
    // Safe name extractor — handles strings, { name, year }, nested objects
    const safeName = (item) => {
      if (!item) return ''
      if (typeof item === 'string') return item
      if (typeof item === 'object') {
        const name = item.name || item.condition || item.label || item.title || ''
        if (typeof name === 'object') return JSON.stringify(name)
        return name ? String(name) : JSON.stringify(item)
      }
      return String(item)
    }
    const safeYear = (item) => {
      if (!item || typeof item !== 'object') return null
      return item.year || item.date || null
    }
    
    // Conditions
    if (profile.medicalHistory.conditions?.length > 0) {
      const conditionList = profile.medicalHistory.conditions
        .map(c => {
          const name = safeName(c)
          const year = safeYear(c)
          return year ? `${name} (${year})` : name
        })
        .filter(Boolean)
        .join(', ')
      if (conditionList) parts.push(`Medical Conditions: ${conditionList}`)
    }
    
    // Surgeries — deduplicate by name+year
    if (profile.medicalHistory.surgeries?.length > 0) {
      const seen = new Set()
      const surgeryList = profile.medicalHistory.surgeries
        .map(s => {
          const name = safeName(s)
          const year = safeYear(s)
          const key = `${name}|${year || ''}`
          if (seen.has(key)) return null
          seen.add(key)
          return year ? `${name} (${year})` : name
        })
        .filter(Boolean)
        .join(', ')
      if (surgeryList) parts.push(`Surgical History: ${surgeryList}`)
    }
    
    if (parts.length === 0) return 'None reported'
    return parts.join('\n')
  }

  // Format family history
  const formatFamilyHistory = () => {
    if (!profile?.familyHistory) return '[Not documented - Complete Profile]'
    
    // Condition name mapping from nameKey to display name
    const conditionNames = {
      'heartDisease': 'Heart Disease',
      'highBloodPressure': 'High Blood Pressure',
      'stroke': 'Stroke',
      'diabetes': 'Diabetes',
      'thyroidDisorder': 'Thyroid Disorder',
      'asthma': 'Asthma',
      'hearingLoss': 'Hearing Loss',
      'allergiesRhinitis': 'Allergies/Rhinitis',
      'glaucoma': 'Glaucoma',
      'macularDegeneration': 'Macular Degeneration',
      'ibd': 'IBD',
      'gerd': 'GERD',
      'arthritis': 'Arthritis',
      'osteoporosis': 'Osteoporosis',
      'eczemaPsoriasis': 'Eczema/Psoriasis',
      'skinCancer': 'Skin Cancer',
      'cancer': 'Cancer',
      'migraines': 'Migraines',
      'depressionAnxiety': 'Depression/Anxiety',
      'kidneyDisease': 'Kidney Disease'
    }
    
    // Check if no known history
    if (profile.familyHistory.noKnownHistory) {
      return 'No significant family history reported'
    }
    
    const parts = []
    
    // Check for conditions with any family member selected
    if (profile.familyHistory.conditions?.length > 0) {
      profile.familyHistory.conditions.forEach(c => {
        const relatives = []
        if (c.mother) relatives.push('Mother')
        if (c.father) relatives.push('Father')
        if (c.sibling) relatives.push('Sibling')
        
        if (relatives.length > 0) {
          // Use nameKey mapping, then name, then format nameKey as fallback
          const displayName = conditionNames[c.nameKey] || c.name || 
            (c.nameKey ? c.nameKey.replace(/([A-Z])/g, ' $1').trim() : c.id || 'Condition')
          parts.push(`${displayName}: ${relatives.join(', ')}`)
        }
      })
    }
    
    // Check other conditions
    if (profile.familyHistory.otherConditions?.length > 0) {
      profile.familyHistory.otherConditions.forEach(c => {
        const relatives = []
        if (c.mother) relatives.push('Mother')
        if (c.father) relatives.push('Father')
        if (c.sibling) relatives.push('Sibling')
        
        if (relatives.length > 0) {
          const displayName = c.name || c.condition || 'Other condition'
          parts.push(`${displayName}: ${relatives.join(', ')}`)
        }
      })
    }
    
    return parts.length > 0 ? parts.join('\n') : 'Non-contributory'
  }

  // Format social history
  const formatSocialHistory = () => {
    if (!profile?.socialLifestyle) return '[Not documented - Complete Profile]'
    const social = profile.socialLifestyle
    const parts = []
    
    if (social.smoking) {
      let tobacco = `Tobacco: ${social.smoking}`
      if (social.smokingFrequency) tobacco += ` (${social.smokingFrequency})`
      parts.push(tobacco)
    }
    if (social.alcohol) {
      let alc = `Alcohol: ${social.alcohol}`
      if (social.alcoholFrequency) alc += ` (${social.alcoholFrequency})`
      parts.push(alc)
    }
    if (social.exercise) {
      parts.push(`Exercise: ${social.exercise}`)
    }
    if (social.occupation) {
      parts.push(`Occupation: ${social.occupation}`)
    }
    
    return parts.length > 0 ? parts.join('\n') : 'Non-contributory'
  }

  // Status chip — minimal, for header
  const StatusChip = ({ complete, label }) => (
    <span 
      className="text-[10px] font-semibold px-2 py-1 rounded-md flex items-center gap-1"
      style={complete 
        ? { background: 'rgba(102,209,112,0.15)', color: '#66D170' }
        : { background: 'rgba(245,158,11,0.15)', color: '#F59E0B' }
      }
    >
      {complete ? <CheckCircle size={10} /> : <Clock size={10} />}
      {label}
    </span>
  )

  // Generate demographics HTML for PDF
  const generateDemographicsHTML = () => {
    const id = profile?.identification || {}
    const ins = profile?.insurance || {}
    const allergies = profile?.allergies || {}
    const meds = profile?.medications || {}
    const medHx = profile?.medicalHistory || {}
    const famHx = profile?.familyHistory || {}
    const social = profile?.socialLifestyle || {}

    // Format address
    const address = [id.streetAddress, id.apt, id.city, id.state, id.zipCode]
      .filter(Boolean)
      .join(', ') || 'Not provided'

    // Format allergies list
    const formatAllergyList = (list) => {
      if (!list || list.length === 0) return 'None reported'
      return list.map(a => `${a.name} (${a.severity}${a.reaction ? ': ' + a.reaction : ''})`).join('; ')
    }

    // Format medications list
    // Format conditions list
    const formatConditions = (conditions) => {
      if (!conditions || conditions.length === 0) return 'None reported'
      // v242.45.0: safeName/safeYear were defined only inside formatMedicalHistory,
      // so this sibling scope threw "Can't find variable: safeName" and crashed the
      // entire Medical Report render (no PDF). Define them locally here.
      const safeName = (item) => {
        if (!item) return ''
        if (typeof item === 'string') return item
        if (typeof item === 'object') {
          const name = item.name || item.condition || item.label || item.title || ''
          if (typeof name === 'object') return JSON.stringify(name)
          return name ? String(name) : JSON.stringify(item)
        }
        return String(item)
      }
      const safeYear = (item) => {
        if (!item || typeof item !== 'object') return null
        return item.year || item.date || null
      }
      return conditions.map(c => {
        const name = safeName(c); const year = safeYear(c)
        return year ? `${name} (${year})` : name
      }).filter(Boolean).join(', ') || 'None reported'
    }

    // Format surgeries list
    const formatSurgeries = (surgeries) => {
      if (!surgeries || surgeries.length === 0) return 'None reported'
      return surgeries.map(s => `${s.name}${s.year ? ' (' + s.year + ')' : ''}`).join('; ')
    }

    // Format family history - uses conditions array structure
    const formatFamilyHx = () => {
      // New structure: conditions is an array of objects with id, nameKey, mother, father, sibling
      if (famHx.conditions && Array.isArray(famHx.conditions)) {
        const selectedConditions = famHx.conditions.filter(c => c.mother || c.father || c.sibling)
        if (selectedConditions.length === 0) {
          return 'None reported'
        }
        return selectedConditions.map(c => {
          const relatives = []
          if (c.mother) relatives.push('Mother')
          if (c.father) relatives.push('Father')
          if (c.sibling) relatives.push('Sibling')
          // Convert nameKey to readable name
          // v242.42.0: nameKey/id can be a hyphenated slug ('heart-disease') or
          // camelCase; humanize either into a readable label. Prefer an explicit name.
          const slug = c.nameKey || c.id || ''
          const name = c.name || c.label ||
            slug.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[-_]/g, ' ')
                .replace(/\s+/g, ' ').trim()
                .replace(/\b\w/g, (ch) => ch.toUpperCase()) || slug
          return `${name} (${relatives.join(', ')})`
        }).join('; ')
      }
      
      // Fallback for old structure
      const conditions = []
      if (famHx.heartDisease) conditions.push('Heart Disease')
      if (famHx.diabetes) conditions.push('Diabetes')
      if (famHx.cancer) conditions.push('Cancer' + (famHx.cancerType ? `: ${famHx.cancerType}` : ''))
      if (famHx.highBloodPressure) conditions.push('High Blood Pressure')
      if (famHx.stroke) conditions.push('Stroke')
      if (famHx.mentalHealth) conditions.push('Mental Health Conditions')
      if (famHx.other) conditions.push(famHx.other)
      return conditions.length > 0 ? conditions.join(', ') : 'None reported'
    }
    
    // Also include other conditions if present
    const formatOtherConditions = () => {
      if (famHx.otherConditions && Array.isArray(famHx.otherConditions) && famHx.otherConditions.length > 0) {
        return famHx.otherConditions.map(c => {
          const relatives = []
          if (c.mother) relatives.push('Mother')
          if (c.father) relatives.push('Father')
          if (c.sibling) relatives.push('Sibling')
          return `${c.name} (${relatives.join(', ')})`
        }).join('; ')
      }
      return ''
    }

    return `
      <div class="demographics-page">
        <h2>Patient Demographics</h2>
        
        <div class="demo-section">
          <h3>Identification</h3>
          <table class="demo-table">
            <tr><td><strong>Name:</strong></td><td>${id.firstName || ''} ${id.lastName || ''}</td></tr>
            <tr><td><strong>Date of Birth:</strong></td><td>${id.dob ? formatDateForDoc(id.dob) : 'Not provided'}</td></tr>
            <tr><td><strong>Age:</strong></td><td>${id.dob ? calculateAge(id.dob) + ' years' : 'Not provided'}</td></tr>
            <tr><td><strong>Sex:</strong></td><td>${formatSex(id.sex) || 'Not provided'}</td></tr>
            <tr><td><strong>Address:</strong></td><td>${address}</td></tr>
          </table>
        </div>

        <div class="demo-section">
          <h3>Insurance Information</h3>
          <table class="demo-table">
            <tr><td><strong>Provider:</strong></td><td>${ins.provider || 'Not provided'}</td></tr>
            <tr><td><strong>Member ID:</strong></td><td>${ins.memberId || 'Not provided'}</td></tr>
            <tr><td><strong>Group Number:</strong></td><td>${ins.groupNumber || 'Not provided'}</td></tr>
          </table>
        </div>

        <div class="demo-section">
          <h3>Allergies</h3>
          <p><strong>Food:</strong> ${allergies.hasFoodAllergies === false ? 'No known food allergies' : (allergies.hasFoodAllergies === true && (!allergies.foodList || allergies.foodList.length === 0) ? 'Has food allergies (not specified)' : formatAllergyList(allergies.foodList))}</p>
          <p><strong>Medication:</strong> ${allergies.hasMedAllergies === false ? 'No known drug allergies' : (allergies.hasMedAllergies === true && (!allergies.medList || allergies.medList.length === 0) ? 'Has drug allergies (not specified)' : formatAllergyList(allergies.medList))}</p>
        </div>

        <div class="demo-section">
          <h3>Current Medications</h3>
          <p>${currentMedicationsLine(meds)}</p>
        </div>

        <div class="demo-section">
          <h3>Medical History</h3>
          <p><strong>Conditions:</strong> ${medHx.hasConditions === false ? 'No chronic conditions' : formatConditions(medHx.conditions)}</p>
          <p><strong>Surgeries:</strong> ${medHx.hasSurgeries === false ? 'No prior surgeries' : formatSurgeries(medHx.surgeries)}</p>
        </div>

        <div class="demo-section">
          <h3>Family History</h3>
          <p>${famHx.noKnownHistory ? 'No significant family history' : formatFamilyHx()}${formatOtherConditions() ? '; ' + formatOtherConditions() : ''}</p>
        </div>

        <div class="demo-section">
          <h3>Social History</h3>
          <p><strong>Smoking:</strong> ${social.smokingStatus || 'Not provided'}</p>
          <p><strong>Alcohol:</strong> ${social.alcoholUse || 'Not provided'}</p>
          <p><strong>Exercise:</strong> ${social.exerciseFrequency || 'Not provided'}</p>
          <p><strong>Occupation:</strong> ${social.occupation || 'Not provided'}</p>
        </div>
      </div>
      
      <div style="page-break-after: always;"></div>
    `
  }

  // Generate and download PDF — clean official letterhead style
  const handleDownloadPDF = async () => {
    const noteContent = document.getElementById('medical-note-content')
    if (!noteContent) return
    

    
    const demographicsHTML = generateDemographicsHTML()
    const caseDate = formatDateMedical(currentCase.finalizedAt) || formatDateMedical(new Date().toISOString())
    
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Patient Medical Report - Case ${currentCase.caseNumber}</title>
        <style>
          body { 
            font-family: -apple-system, 'Segoe UI', 'Inter', sans-serif; 
            padding: 40px; 
            max-width: 800px; 
            margin: 0 auto; 
            color: #1E293B;
            font-size: 11px;
            line-height: 1.6;
          }
          .pdf-header {
            padding-bottom: 12px;
            margin-bottom: 16px;
            border-bottom: 2px solid #15354F;
          }
          .pdf-logo { height: 30px; width: auto; display: block; margin-bottom: 8px; }
          .pdf-header-title {
            font-size: 12px;
            font-weight: 700;
            letter-spacing: 1px;
            text-transform: uppercase;
            color: #15354F;
          }
          .pdf-header-meta {
            font-size: 10px;
            color: #64748B;
            margin-top: 2px;
          }
          .pdf-demographics {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            gap: 2px 16px;
            font-size: 10px;
            padding-bottom: 10px;
            margin-bottom: 12px;
            border-bottom: 1px solid #E2E8F0;
          }
          .section-header {
            font-size: 10px;
            font-weight: 700;
            letter-spacing: 0.5px;
            text-transform: uppercase;
            color: #15354F;
            border-bottom: 1.5px solid #15354F;
            padding-bottom: 3px;
            margin-top: 22px;
            margin-bottom: 9px;
          }
          .section-header:first-of-type { margin-top: 0; }
          p { margin: 3px 0; }
          strong { color: #0F172A; }
          .signature-block {
            margin-top: 24px;
            padding-top: 10px;
            border-top: 1.5px solid #15354F;
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
          }
          .pdf-footer { margin-top: 22px; }
          .pdf-footer-bar {
            padding-top: 12px;
            border-top: 1px solid #E2E8F0;
            display: flex;
            justify-content: space-between;
            align-items: baseline;
          }
          .pdf-powered { display: flex; align-items: baseline; gap: 8px; }
          .pdf-powered span { font-size: 10px; font-weight: 600; color: #64748B; letter-spacing: 0.01em; }
          .pdf-powered img { height: 18px; width: auto; position: relative; top: 3px; }
          .pdf-footer-caption { font-size: 9.5px; color: #64748B; }
          .pdf-footer-phi { margin-top: 8px; font-size: 8px; color: #94A3B8; text-align: center; line-height: 1.5; }
          .demographics-page h2 { 
            font-size: 12px; 
            color: #15354F; 
            border-bottom: 1.5px solid #15354F; 
            padding-bottom: 5px; 
            margin-top: 26px;
            margin-bottom: 10px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          .demographics-page h3 { 
            font-size: 11px; 
            color: #15354F; 
            margin-top: 18px; 
            margin-bottom: 7px; 
          }
          .demo-table td { padding: 3px 8px 3px 0; vertical-align: top; }
          .demo-table td:first-child { width: 130px; color: #64748B; }
          @media print { 
            body { padding: 20px; } 
            .page-break { page-break-after: always; }
          }
        </style>
      </head>
      <body>
        <div class="pdf-header">
          <img class="pdf-logo" src="${ORELLA_WORDMARK_NAVY}" alt="Orella" />
          <div class="pdf-header-title">Patient Medical Report</div>
          <div class="pdf-header-meta">Case ${currentCase.caseNumber} &middot; ${caseDate}</div>
        </div>
        
        <div class="pdf-demographics">
          <p><strong>Patient:</strong> ${getPatientName()}</p>
          <p><strong>DOB:</strong> ${formatDateForDoc(profile?.identification?.dob)}</p>
          <p><strong>Age/Sex:</strong> ${calculateAge(profile?.identification?.dob)}/${(formatSex(profile?.identification?.sex) || '?').charAt(0)}</p>
          <p><strong>Complaint:</strong> ${focusAreaName}</p>
          <p><strong>Date:</strong> ${caseDate}</p>
          <p><strong>Provider:</strong> ${doctorName ? doctorName.replace('Dr. ', '').split(',')[0] : 'Pending'}</p>
        </div>
        
        ${demographicsHTML}
        
        ${noteContent.innerHTML}
        
        <div class="pdf-footer">
          <div class="pdf-footer-bar">
            <div class="pdf-powered"><span>Powered by</span><img src="${ORELLA_WORDMARK_NAVY}" alt="Orella" /></div>
            <div class="pdf-footer-caption">Confidential Medical Information</div>
          </div>
          <p class="pdf-footer-phi">This document contains protected health information (PHI). Handle in accordance with HIPAA regulations.</p>
        </div>
      </body>
      </html>
    `

    if (Capacitor.isNativePlatform()) {
      await sharePdfFromHtml(html, 'Medical Note')
      return
    }
    const printWindow = window.open('', '_blank')
    if (!printWindow) {
      alert(t('errors2.popupsForPdf'))
      return
    }
    printWindow.document.write(html)
    
    printWindow.document.close()
    setTimeout(() => { printWindow.print() }, 500)
  }

  // Section header style — navy uppercase with underline
  const SectionHeader = ({ children }) => (
    <h3 
      className="text-[11px] font-bold tracking-widest uppercase mt-5 mb-2 pb-1"
      style={{ color: '#15354F', borderBottom: '1.5px solid #15354F' }}
    >
      {children}
    </h3>
  )

  // Document line style
  const DocLine = ({ label, children, italic, muted }) => (
    <p 
      className="text-[12.5px] leading-relaxed"
      style={{ 
        color: muted ? '#64748B' : '#1E293B', 
        fontStyle: italic ? 'italic' : 'normal' 
      }}
    >
      {label && <span className="font-semibold" style={{ color: '#0F172A' }}>{label}</span>}
      {children}
    </p>
  )

  // Pending notice — minimal amber text
  const PendingNotice = ({ message }) => (
    <p className="text-[12px] italic" style={{ color: '#D97706' }}>
      [{message || 'Pending Doctor Review'}]
    </p>
  )

  // Format a full date for display
  // formatFullDate: "January 15, 2025"
  const formatFullDate = (dateString) => formatDate(dateString, { month: 'long', day: 'numeric', year: 'numeric' })
  
  // formatDateTime imported from dateUtils

  return (
    <div className="min-h-full bg-white flex flex-col">
      {/* ===== NAVY HEADER BAR ===== */}
      <div className="px-4 pt-4 pb-3 sticky top-0 z-10" style={{ background: '#15354F', paddingTop: 'calc(1rem + env(safe-area-inset-top, 0px))' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/case/home')} className="p-1 -ml-1">
              <ArrowLeft size={20} color="white" />
            </button>
            <h1 className="text-[16px] font-bold text-white tracking-tight">Medical Report</h1>
          </div>
          <div className="flex items-center gap-1.5">
            <button 
              onClick={() => setShowHIPAAModal(true)}
              className="px-3 py-1.5 rounded-lg text-[11px] font-semibold text-white flex items-center gap-1.5"
              style={{ background: 'rgba(255,255,255,0.12)' }}
            >
              <Download size={13} />
              PDF
            </button>
          </div>
        </div>
        {/* Status chips */}
        <div className="flex gap-2 mt-2.5">
          <StatusChip complete={describeComplete} label="Describe" />
          <StatusChip complete={detectComplete} label="Detect" />
          <StatusChip complete={diagnoseComplete || isFinalized} label="Diagnose" />
          <StatusChip complete={doctorConsultComplete} label="Reviewed" />
        </div>
      </div>

      {/* ===== DOCUMENT BODY ===== */}
      <div className="flex-1 overflow-auto">
        <div id="medical-note-content" className="px-5 pt-5 pb-32">

          {/* Document Title Block */}
          <div className="text-center mb-5 pb-4" style={{ borderBottom: '2px solid #15354F' }}>
            <p className="text-[13px] font-bold tracking-widest uppercase mb-1" style={{ color: '#15354F' }}>
              Orella Health
            </p>
            <h2 className="text-[17px] font-extrabold tracking-tight mb-1" style={{ color: '#0F172A' }}>
              Patient Medical Report
            </h2>
            <p className="text-[11px] text-gray-400 font-medium">
              Case {currentCase.caseNumber} — {isFinalized ? `Finalized ${formatFullDate(currentCase.finalizedAt)}` : `Created ${formatFullDate(currentCase.createdAt)}`}
            </p>
          </div>

          {/* ===== IF FINALIZED: Show consultation note directly ===== */}
          {isFinalized && (consultNote || anyEncounterHeld) ? (
            <>
              {/* Patient Info Grid */}
              <SectionHeader>Patient Information</SectionHeader>
              <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mb-1">
                <DocLine label="Name: ">{getPatientName()}</DocLine>
                <DocLine label="DOB: ">{formatDateForDoc(profile?.identification?.dob)}</DocLine>
                <DocLine label="Age: ">{calculateAge(profile?.identification?.dob)}</DocLine>
                <DocLine label="Sex: ">{formatSex(profile?.identification?.sex) || '[Not provided]'}</DocLine>
              </div>
              <DocLine label="Chief Complaint: ">{focusAreaName}</DocLine>
              <DocLine label="Date of Service: ">{formatFullDate(currentCase.finalizedAt || currentCase.createdAt)}</DocLine>
              {doctorName && <DocLine label="Clinician: ">{doctorName}</DocLine>}

              {/* v132.2: Encounter-Sequential Notes — all encounters in chronological order */}
              {(() => {
                const encounters = currentCase.encounters || []
                const sortedEncs = [...encounters].sort((a, b) => (a.encounter_number || a.encounterNumber || 0) - (b.encounter_number || b.encounterNumber || 0))
                
                // If we have encounters with notes, render them sequentially
                // ★ PROD-1077: a signed encounter whose note is held stays in the list (as the held card)
                const encNotes = sortedEncs.filter(enc => {
                  const note = enc.consult_note || enc.consultNote
                  return enc.finalized && (note || isEncounterNoteHeld(enc))
                })
                
                // Fallback: if no encounter-level notes, use case-level notes (legacy)
                if (encNotes.length === 0) {
                  // Legacy: initial + follow-up from case-level fields
                  const allNotes = []
                  if (initialConsultNote || priorConsultNote) allNotes.push({ label: 'Initial Consultation', note: initialConsultNote || priorConsultNote, date: consultNoteHistory[0]?.date })
                  if (consultNote) allNotes.push({ label: isFollowUpCase ? 'Follow-Up Consultation' : 'Consultation Note', note: consultNote, date: currentCase.finalizedAt })
                  
                  return allNotes.map((entry, nIdx) => (
                    <div key={nIdx}>
                      {nIdx > 0 && (
                        <div className="flex items-center gap-4 my-5">
                          <div className="flex-1 h-px" style={{ background: '#15354F' }} />
                          <span className="text-[10px] font-bold tracking-widest uppercase" style={{ color: '#15354F' }}>Encounter {nIdx + 1}</span>
                          <div className="flex-1 h-px" style={{ background: '#15354F' }} />
                        </div>
                      )}
                      <SectionHeader>{entry.label}</SectionHeader>
                      {entry.date && <p className="text-[11px] text-gray-400 mb-2">{formatFullDate(entry.date)}</p>}
                      <div className={`text-[${nIdx === allNotes.length - 1 ? '12.5' : '12'}px] leading-relaxed whitespace-pre-wrap`} style={{ color: nIdx === allNotes.length - 1 ? '#1E293B' : '#475569' }}>
                        {entry.note
                          .replace(/[━═─┈┉┄┅╌╍]/g, '')
                          .split('\n')
                          // R14 / PROD-260 (review r1 S1): no bracketed ICD-10 code in a rendered line
                          .map((line) => stripBracketedIcd(line))
                          .map((line, idx) => {
                            if (line.match(/^(PATIENT INFORMATION|HISTORY OF PRESENT ILLNESS|REVIEW OF SYSTEMS|PAST MEDICAL HISTORY|PAST SURGICAL HISTORY|CURRENT MEDICATIONS|ALLERGIES|FAMILY HISTORY|SOCIAL HISTORY|PHYSICAL EXAMINATION|ASSESSMENT|PLAN|HISTORY AND PHYSICAL EXAMINATION)$/)) {
                              return <div key={idx} className="text-[11px] font-bold tracking-widest uppercase mt-4 mb-1 pb-1" style={{ color: nIdx === allNotes.length - 1 ? '#15354F' : '#64748B', borderBottom: `1px solid ${nIdx === allNotes.length - 1 ? '#15354F' : '#CBD5E1'}` }}>{line}</div>
                            }
                            if (line.match(/^[═─\-=]+$/)) return null
                            if (line.match(/^(Name:|DOB:|Chief Complaint:|Date of Service:|General:|Eyes:|Ears:|Nose:|Throat:|Neck:|Primary Diagnosis:|Differential Diagnoses:|Vitals:|Respiratory:|Cardiovascular:|Neurological:|Skin:|Head:)/)) {
                              const ci = line.indexOf(':')
                              return <div key={idx} className="mt-0.5"><span className="font-semibold" style={{ color: '#0F172A' }}>{line.substring(0, ci + 1)}</span>{line.substring(ci + 1)}</div>
                            }
                            if (!line.trim()) return <div key={idx} className="h-1.5" />
                            return <div key={idx}>{line}</div>
                          })}
                      </div>
                    </div>
                  ))
                }
                
                // Encounter-level notes — render each with divider
                const catLabels = { initial: 'Initial Consultation', follow_up: 'Follow-Up Visit', observation: 'Observation', complete: 'Case Closure' }
                
                return encNotes.map((enc, nIdx) => {
                  const encNum = enc.encounter_number || enc.encounterNumber
                  // ★ PROD-1077: the held branch wins, even if an old API still sends the text
                  const held = isEncounterNoteHeld(enc)
                  const note = held ? '' : (enc.consult_note || enc.consultNote || '')
                  const category = enc.category || 'initial'
                  const encDate = enc.finalized_at || enc.finalizedAt || enc.created_at || enc.createdAt
                  
                  return (
                    <div key={encNum}>
                      {nIdx > 0 && (
                        <div className="flex items-center gap-4 my-5">
                          <div className="flex-1 h-px" style={{ background: '#15354F' }} />
                          <span className="text-[10px] font-bold tracking-widest uppercase" style={{ color: '#15354F' }}>Encounter {encNum}</span>
                          <div className="flex-1 h-px" style={{ background: '#15354F' }} />
                        </div>
                      )}
                      <SectionHeader>{catLabels[category] || `Encounter ${encNum}`}</SectionHeader>
                      {encDate && <p className="text-[11px] text-gray-400 mb-2">{formatFullDate(encDate)}</p>}
                      {held ? (
                        <HeldNoteCard doctorName={doctorName} officeName={officeName} officeAddress={officeAddress} officePhone={officePhone} />
                      ) : (
                      <div className="text-[12.5px] leading-relaxed whitespace-pre-wrap" style={{ color: '#1E293B' }}>
                        {note
                          .replace(/[━═─┈┉┄┅╌╍]/g, '')
                          .split('\n')
                          // R14 / PROD-260 (review r1 S1): no bracketed ICD-10 code in a rendered line
                          .map((line) => stripBracketedIcd(line))
                          .map((line, idx) => {
                            if (line.match(/^(PATIENT INFORMATION|HISTORY OF PRESENT ILLNESS|REVIEW OF SYSTEMS|PAST MEDICAL HISTORY|PAST SURGICAL HISTORY|CURRENT MEDICATIONS|ALLERGIES|FAMILY HISTORY|SOCIAL HISTORY|PHYSICAL EXAMINATION|ASSESSMENT|PLAN|HISTORY AND PHYSICAL EXAMINATION)$/)) {
                              return <div key={idx} className="text-[11px] font-bold tracking-widest uppercase mt-5 mb-1.5 pb-1" style={{ color: '#15354F', borderBottom: '1.5px solid #15354F' }}>{line}</div>
                            }
                            if (line.match(/^[═─\-=]+$/)) return null
                            if (line.match(/^(Name:|DOB:|Chief Complaint:|Date of Service:|General:|Eyes:|Ears:|Nose:|Throat:|Neck:|Primary Diagnosis:|Differential Diagnoses:|Vitals:|Respiratory:|Cardiovascular:|Neurological:|Skin:|Head:)/)) {
                              const ci = line.indexOf(':')
                              return <div key={idx} className="mt-0.5"><span className="font-semibold" style={{ color: '#0F172A' }}>{line.substring(0, ci + 1)}</span>{line.substring(ci + 1)}</div>
                            }
                            if (!line.trim()) return <div key={idx} className="h-1.5" />
                            return <div key={idx}>{line}</div>
                          })}
                      </div>
                      )}
                    </div>
                  )
                })
              })()}
              {/* End of encounter-sequential notes */}

              {/* Billing Codes */}
              {(diagnosisTexts.length > 0 || cptCodes.length > 0) && (
                <>
                  <SectionHeader>Billing Codes</SectionHeader>
                  <div className="grid grid-cols-2 gap-x-6">
                    {/* R14 / PROD-260 (patient P1): the diagnosis text stays, the raw ICD-10
                        code does not reach the patient (the API strip follows in D5c). The column
                        keeps a label (review r1 N6), naming what it lists now. */}
                    {diagnosisTexts.length > 0 && (
                      <div>
                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Diagnosis</p>
                        {diagnosisTexts.map((text, idx) => (
                          <DocLine key={idx}>{text}</DocLine>
                        ))}
                      </div>
                    )}
                    {cptCodes.length > 0 && (
                      <div>
                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">CPT</p>
                        {cptCodes.map((code, idx) => (
                          <DocLine key={idx}><span className="font-semibold" style={{ color: '#15354F' }}>{code.code}</span> — {code.description}</DocLine>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Follow-Up Information */}
              {followUpData.type && (
                <>
                  <SectionHeader>Follow-Up</SectionHeader>
                  {(followUpData.type === 'in_follow_up' || followUpData.type === 'followup') && (
                    <DocLine label="Recommendation: ">Return for re-evaluation in {asText(followUpData.timeframe) || 'as scheduled'}</DocLine>
                  )}
                  {(followUpData.type === 'completed' || followUpData.type === 'complete' || followUpData.type === 'pcp') && (
                    <DocLine label="Status: ">Care complete — no follow-up required at this time</DocLine>
                  )}
                  {followUpData.type === 'observation' && (
                    <DocLine label="Observation: ">Continue to monitor symptoms at home. Return if worsening.</DocLine>
                  )}
                  {(followUpData.type === 'referral' || followUpData.type === 'refer') && (
                    <DocLine label="Referral: ">Referred to {asText(followUpData.referral?.specialty) || 'specialist'}{asText(followUpData.referral?.doctorName) ? ` — ${asText(followUpData.referral.doctorName)}` : ''}</DocLine>
                  )}
                  {followUpData.visitType && asText(followUpData.visitType) && (
                    <DocLine label="Visit Type: ">{followUpData.visitType === 'in-person' ? 'In-Person' : followUpData.visitType === 'virtual' ? 'Virtual/Telehealth' : asText(followUpData.visitType)}</DocLine>
                  )}
                  {followUpData.notes && asText(followUpData.notes) && <DocLine label="Notes: ">{asText(followUpData.notes)}</DocLine>}
                </>
              )}

              {/* Signature Block */}
              <div className="mt-6 pt-3 flex justify-between items-end" style={{ borderTop: '1.5px solid #15354F' }}>
                <div>
                  {doctorName && <p className="text-[12.5px] font-bold" style={{ color: '#0F172A' }}>{doctorName}</p>}
                  <p className="text-[11px] text-gray-500">{specialtyName ? `${specialtyName}` : ''}</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] text-gray-400">Electronically signed</p>
                  <p className="text-[11px] text-gray-500">{formatDateTime(currentCase.finalizedAt)}</p>
                </div>
              </div>

              {/* After Visit Summary — same clean style, separated */}
              {avsContent && (
                <div className="mt-8 pt-5" style={{ borderTop: '2px solid #15354F' }}>
                  <p className="text-[13px] font-bold tracking-widest uppercase mb-1 text-center" style={{ color: '#15354F' }}>
                    Orella Health
                  </p>
                  <h3 className="text-[15px] font-extrabold text-center mb-1" style={{ color: '#0F172A' }}>
                    After Visit Summary
                  </h3>
                  <p className="text-[11px] text-gray-400 text-center mb-4">
                    Prepared for: {getPatientName()} — {formatFullDate(currentCase.finalizedAt)}
                  </p>
                  
                  {/* Parse and render AVS sections */}
                  {(() => {
                    const avsSections = formatAVSContent(avsContent)
                    if (avsSections) {
                      return avsSections.map((section, idx) => (
                        <div key={idx}>
                          <SectionHeader>{section.title}</SectionHeader>
                          {section.content.map((item, i) => (
                            <DocLine key={i}>— {item}</DocLine>
                          ))}
                        </div>
                      ))
                    }
                    // Fallback: render as plain paragraphs
                    return (
                      <>
                        <SectionHeader>Visit Summary</SectionHeader>
                        {avsContent.split('\n')
                          .filter(line => line.trim() && !line.match(/^[━═─┈┉┄┅╌╍]+$/))
                          .map((line, idx) => (
                            <DocLine key={idx}>{line.replace(/[━═─┈┉┄┅╌╍]/g, '').trim()}</DocLine>
                          ))
                        }
                      </>
                    )
                  })()}

                  <div className="mt-5 pt-3 text-center" style={{ borderTop: '1px solid #E2E8F0' }}>
                    <p className="text-[11px] text-gray-400">Questions? Contact your doctor through the Orella app.</p>
                  </div>
                </div>
              )}
            </>
          ) : (
            /* ===== PRE-CONSULT: Building report from patient data ===== */
            <>
              {/* Patient Information */}
              <SectionHeader>Patient Information</SectionHeader>
              <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mb-1">
                <DocLine label="Name: ">{getPatientName()}</DocLine>
                <DocLine label="DOB: ">{formatDateForDoc(profile?.identification?.dob)}</DocLine>
                <DocLine label="Age: ">{calculateAge(profile?.identification?.dob)}</DocLine>
                <DocLine label="Sex: ">{formatSex(profile?.identification?.sex) || '[Not provided]'}</DocLine>
              </div>
              <DocLine label="Chief Complaint: ">{focusAreaName}</DocLine>
              <DocLine label="Date of Visit: ">{formatFullDate(currentCase.createdAt)}</DocLine>

              {/* HPI */}
              <SectionHeader>History of Present Illness</SectionHeader>
              <p className="text-[12.5px] leading-relaxed whitespace-pre-wrap" style={{ color: '#1E293B' }}>
                {generateHPI()}
              </p>

              {/* ROS — heading deliberately NOT "Review of Systems". That phrase
                  means asked and answered, and under it an omission reads as a
                  denial. This list is what the patient volunteered, nothing more. */}
              <SectionHeader>Symptoms Reported by System</SectionHeader>
              {(() => {
                const ros = generateROS()
                if (ros.length === 0) {
                  return <PendingNotice message="No systems assessed — the patient has not reported symptoms that map to a body system." />
                }
                return ros.map((entry, idx) => <DocLine key={idx}>{entry}</DocLine>)
              })()}
              <DocLine muted italic>Only systems the patient reported on are listed. A system that does not appear was not assessed, and must not be read as negative.</DocLine>
              {!describeComplete && (
                <PendingNotice message="Complete Describe section for full symptom reporting" />
              )}

              {/* Past Medical History */}
              <SectionHeader>Past Medical History</SectionHeader>
              <p className="text-[12.5px] leading-relaxed whitespace-pre-wrap" style={{ color: '#1E293B' }}>
                {formatMedicalHistory()}
              </p>

              {/* Medications */}
              <SectionHeader>Current Medications</SectionHeader>
              <p className="text-[12.5px] leading-relaxed whitespace-pre-wrap" style={{ color: '#1E293B' }}>
                {formatMedications()}
              </p>

              {/* Allergies */}
              <SectionHeader>Allergies</SectionHeader>
              <p className="text-[12.5px] leading-relaxed whitespace-pre-wrap" style={{ color: '#1E293B' }}>
                {formatAllergies()}
              </p>

              {/* Family History */}
              <SectionHeader>Family History</SectionHeader>
              <p className="text-[12.5px] leading-relaxed whitespace-pre-wrap" style={{ color: '#1E293B' }}>
                {formatFamilyHistory()}
              </p>

              {/* Social History */}
              <SectionHeader>Social History</SectionHeader>
              <p className="text-[12.5px] leading-relaxed whitespace-pre-wrap" style={{ color: '#1E293B' }}>
                {formatSocialHistory()}
              </p>

              {/* Physical Examination */}
              <SectionHeader>Physical Examination</SectionHeader>
              {/* Patient uploads */}
              {(patientPhotos.length > 0 || patientDocuments.length > 0) && (
                <div className="mb-2">
                  {patientPhotos.length > 0 && (
                    <DocLine label="Patient-Submitted: ">{patientPhotos.length} photo(s) uploaded for review</DocLine>
                  )}
                  {patientDocuments.length > 0 && (
                    <DocLine label="Documents: ">{patientDocuments.length} document(s) uploaded for review</DocLine>
                  )}
                </div>
              )}
              {doctorConsultComplete && examinationNotes ? (
                <p className="text-[12.5px] leading-relaxed whitespace-pre-wrap" style={{ color: '#1E293B' }}>
                  {examinationNotes}
                </p>
              ) : (
                <PendingNotice message="Physical examination findings pending doctor review" />
              )}

              {/* Assessment */}
              <SectionHeader>Assessment</SectionHeader>
              {doctorConsultComplete ? (
                <div>
                  {/* R14 / PROD-260: diagnosis text only, no raw ICD-10 code */}
                  {diagnosisTexts.length > 0 ? (
                    diagnosisTexts.map((text, idx) => (
                      <DocLine key={idx}>{idx + 1}. {text}</DocLine>
                    ))
                  ) : (
                    <DocLine>1. {focusAreaName || 'Condition under evaluation'}</DocLine>
                  )}
                </div>
              ) : (
                <PendingNotice message="Share your case with a doctor to receive clinical assessment" />
              )}

              {/* Plan */}
              <SectionHeader>Plan</SectionHeader>
              {doctorConsultComplete ? (
                <div>
                  {(followUpData.type === 'in_follow_up' || followUpData.type === 'followup') && (
                    <DocLine label="Follow-up: ">Return for re-evaluation in {asText(followUpData.timeframe) || 'as scheduled'}</DocLine>
                  )}
                  {(followUpData.type === 'completed' || followUpData.type === 'complete' || followUpData.type === 'pcp') && (
                    <DocLine label="Status: ">Care complete — no follow-up required</DocLine>
                  )}
                  {followUpData.type === 'observation' && (
                    <DocLine label="Observation: ">Continue to monitor symptoms at home</DocLine>
                  )}
                  {(followUpData.type === 'referral' || followUpData.type === 'refer') && (
                    <DocLine label="Referral: ">Referred to {asText(followUpData.referral?.specialty) || 'specialist'}</DocLine>
                  )}
                  {followUpData.notes && asText(followUpData.notes) && <DocLine label="Notes: ">{asText(followUpData.notes)}</DocLine>}
                  {!followUpData.type && <DocLine>See consultation note for treatment plan details.</DocLine>}
                </div>
              ) : (
                <PendingNotice message="Treatment plan pending doctor consultation" />
              )}

              {/* Signature / Note Generated */}
              <div className="mt-6 pt-3" style={{ borderTop: '1.5px solid #15354F' }}>
                <p className="text-[11px] text-gray-400">
                  Report generated: {formatDateTime(new Date().toISOString())}
                </p>
                {doctorConsultComplete && doctorName && (
                  <p className="text-[12.5px] font-bold mt-1" style={{ color: '#0F172A' }}>{doctorName}</p>
                )}
              </div>
            </>
          )}

        </div>
      </div>

      {/* HIPAA Disclaimer Modal */}
      <HIPAADisclaimerModal
        isOpen={showHIPAAModal}
        onClose={() => setShowHIPAAModal(false)}
        onConfirm={handleDownloadPDF}
      />
    </div>
  )
}
